import { useEffect, useState } from "react";
import { Card } from "./ui/card";
import { StarRow } from "./FeedbackPanel";
import { Star, TrendingUp, MessageSquare } from "lucide-react";

/**
 * MyRatingsCard — Doctor Dashboard block: this-month + all-time + last 5
 * comments + a lightweight sparkline trend of daily averages.
 */
export default function MyRatingsCard({ api }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/doctors/me/feedback")
      .then((r) => setData(r.data))
      .catch(() => setData({ month: { avg: null, count: 0 }, total: { avg: null, count: 0 }, recent: [], trend: [] }));
  }, [api]);

  if (!data) return null;

  return (
    <Card className="medical-card p-4 sm:p-5" data-testid="doctor-my-ratings">
      <div className="flex items-center gap-2 mb-3">
        <Star className="w-4 h-4 text-amber-500" />
        <h2 className="font-heading text-lg font-semibold text-slate-900">My ratings</h2>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="rounded-lg border border-slate-200 p-3" data-testid="ratings-month">
          <div className="text-[10px] uppercase tracking-wider text-slate-500">This month</div>
          <div className="mt-1 flex items-center gap-2">
            <span className="font-heading text-2xl font-bold text-amber-600">
              {data.month.avg != null ? `${data.month.avg.toFixed(1)}` : "—"}
            </span>
            <Star className="w-4 h-4 text-amber-500 fill-amber-400" />
            <span className="text-xs text-slate-500 ml-auto">{data.month.count} reviews</span>
          </div>
        </div>
        <div className="rounded-lg border border-slate-200 p-3" data-testid="ratings-total">
          <div className="text-[10px] uppercase tracking-wider text-slate-500">All-time</div>
          <div className="mt-1 flex items-center gap-2">
            <span className="font-heading text-2xl font-bold text-slate-800">
              {data.total.avg != null ? `${data.total.avg.toFixed(1)}` : "—"}
            </span>
            <Star className="w-4 h-4 text-slate-400 fill-slate-300" />
            <span className="text-xs text-slate-500 ml-auto">{data.total.count} reviews</span>
          </div>
        </div>
      </div>

      {/* Trend sparkline */}
      {data.trend && data.trend.length >= 2 && (
        <div className="mt-4">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1 flex items-center gap-1">
            <TrendingUp className="w-3 h-3" /> Trend
          </div>
          <Sparkline points={data.trend.map((t) => t.avg)} />
        </div>
      )}

      {/* Recent comments */}
      <div className="mt-4">
        <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-1">
          <MessageSquare className="w-3 h-3" /> Last 5 comments (anonymised)
        </div>
        {data.recent.length === 0 ? (
          <p className="text-xs text-slate-400 italic" data-testid="ratings-recent-empty">No comments yet.</p>
        ) : (
          <div className="space-y-2">
            {data.recent.map((r, i) => (
              <div key={i} className="rounded border border-slate-100 bg-slate-50/40 px-3 py-2 flex items-start gap-3" data-testid={`ratings-recent-${i}`}>
                <div className="w-7 h-7 rounded-full bg-slate-800 text-white text-[10px] font-bold flex items-center justify-center shrink-0">{r.initials}</div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <StarRow rating={r.rating} />
                    <span className="text-[11px] text-slate-400">{r.created_at?.slice(0, 10)}</span>
                  </div>
                  {r.comment && <div className="text-sm text-slate-700 mt-0.5">{r.comment}</div>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Card>
  );
}

function Sparkline({ points }) {
  if (!points || points.length < 2) return null;
  const w = 240, h = 40, pad = 4;
  const clean = points.map((p) => p ?? 0);
  const min = Math.min(...clean), max = Math.max(...clean);
  const range = Math.max(0.001, max - min);
  const step = (w - pad * 2) / (clean.length - 1);
  const d = clean.map((v, i) => {
    const x = pad + i * step;
    const y = pad + (h - pad * 2) * (1 - (v - min) / range);
    return `${i === 0 ? "M" : "L"} ${x.toFixed(1)} ${y.toFixed(1)}`;
  }).join(" ");
  return (
    <svg viewBox={`0 0 ${w} ${h}`} width="100%" height="40" className="text-emerald-500" data-testid="ratings-trend-sparkline">
      <path d={d} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
